import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { fmtDate } from "../lib/status";
import { toast } from "sonner";
import { MessageSquareWarning, CheckCircle2, AlertTriangle, XCircle, PencilLine } from "lucide-react";

const CHANGE_TYPE_LABELS = {
  pickup_date: "Pickup date", pickup_time: "Pickup time",
  return_date: "Return date", return_time: "Return time",
  vehicle: "Vehicle", pickup_location: "Pickup location",
  return_location: "Return location", other: "Other",
};

function pad(n) { return String(n).padStart(2, "0"); }
function toParts(iso) {
  const d = new Date(iso);
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}
function mergeDateTime(currentIso, newDate, newTime) {
  const cur = toParts(currentIso);
  return new Date(`${newDate || cur.date}T${newTime || cur.time}:00`).toISOString();
}

/**
 * Gap-closure item 2 (staff side): the previously-missing "current vs
 * requested" comparison + Approve Change / Propose Different Option /
 * Decline Change actions for a `change_requested` booking. All three
 * actions reuse the exact same real PUT /bookings/{id}?resend_confirmation
 * path already used by the plain "Resend confirmation" button (see
 * BookingDetail.jsx's `resend`) - the only difference is what field values
 * get submitted. No new backend endpoint, no duplicated conflict logic
 * (the shared PUT handler already runs the real conflict/override-note
 * gate on any item_id/date change).
 */
export default function ChangeRequestReviewPanel({ bookingId, booking, onChanged }) {
  // All hooks run unconditionally, before the `!booking?.change_requested`
  // early return below, so this stays valid whether or not the caller
  // gates rendering itself (React's rules of hooks: same hooks, same order,
  // every render).
  const requested = booking?.client_change_request?.requested_values || {};
  const changeTypes = booking?.client_change_request?.change_types || [];
  const note = booking?.client_change_message;

  const [mode, setMode] = useState(null); // null | "propose"
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [overrideNote, setOverrideNote] = useState("");
  const [fields, setFields] = useState(() => (booking ? {
    pickup_date: requested.pickup_date || toParts(booking.start_datetime).date,
    pickup_time: requested.pickup_time || toParts(booking.start_datetime).time,
    return_date: requested.return_date || toParts(booking.end_datetime).date,
    return_time: requested.return_time || toParts(booking.end_datetime).time,
    pickup_location: requested.pickup_location ?? booking.pickup_location ?? "",
    return_location: requested.return_location ?? booking.return_location ?? "",
  } : {}));
  const setField = (k) => (e) => { setFields((f) => ({ ...f, [k]: e.target.value })); setCheckResult(null); };

  if (!booking?.change_requested) return null;

  function buildUpdates(source) {
    const updates = {};
    if (changeTypes.includes("pickup_date") || changeTypes.includes("pickup_time")) {
      updates.start_datetime = mergeDateTime(booking.start_datetime,
        source === "requested" ? requested.pickup_date : fields.pickup_date,
        source === "requested" ? requested.pickup_time : fields.pickup_time);
    }
    if (changeTypes.includes("return_date") || changeTypes.includes("return_time")) {
      updates.end_datetime = mergeDateTime(booking.end_datetime,
        source === "requested" ? requested.return_date : fields.return_date,
        source === "requested" ? requested.return_time : fields.return_time);
    }
    if (changeTypes.includes("pickup_location")) {
      updates.pickup_location = source === "requested" ? requested.pickup_location : fields.pickup_location;
    }
    if (changeTypes.includes("return_location")) {
      updates.return_location = source === "requested" ? requested.return_location : fields.return_location;
    }
    return updates;
  }

  const checkAvailability = async () => {
    const updates = buildUpdates(mode === "propose" ? "form" : "requested");
    if (!updates.start_datetime && !updates.end_datetime) {
      toast("This change doesn't affect dates: nothing to check.");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.post("/bookings/check-conflict", {
        item_id: booking.item_id,
        start_datetime: updates.start_datetime || booking.start_datetime,
        end_datetime: updates.end_datetime || booking.end_datetime,
        exclude_id: bookingId,
      });
      setCheckResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setChecking(false);
    }
  };

  const submit = async (updates, { successMsg }) => {
    setBusy(true);
    try {
      await api.put(`/bookings/${bookingId}`, {
        ...updates, resend_confirmation: true,
        override_note: overrideNote || undefined,
      });
      toast.success(successMsg);
      setMode(null); setCheckResult(null); setOverrideNote("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approveChange = () => submit(buildUpdates("requested"), { successMsg: "Change approved. Updated confirmation sent to the customer." });
  const proposeDifferent = () => submit(buildUpdates("form"), { successMsg: "Your proposed terms were sent to the customer to confirm." });
  const declineChange = () => submit({}, { successMsg: "The original terms were re-sent for the customer to confirm." });

  const needsOverride = checkResult?.has_critical || checkResult?.has_warning;

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-5" data-testid="change-request-review-panel">
      <div className="flex items-center gap-2">
        <MessageSquareWarning className="w-5 h-5 text-amber-700" />
        <h2 className="font-display text-lg font-semibold text-amber-900">Customer requested a change</h2>
      </div>

      {changeTypes.length > 0 && (
        <div className="mt-3 space-y-2">
          {changeTypes.filter((ct) => ct !== "other").map((ct) => {
            const current = ct === "pickup_date" || ct === "pickup_time" ? fmtDate(booking.start_datetime)
              : ct === "return_date" || ct === "return_time" ? fmtDate(booking.end_datetime)
              : ct === "pickup_location" ? (booking.pickup_location || "—")
              : ct === "return_location" ? (booking.return_location || "—")
              : ct === "vehicle" ? (booking.vehicle?.name || "—") : "—";
            const req = ct === "pickup_date" || ct === "pickup_time" ? (requested.pickup_date || requested.pickup_time ? `${requested.pickup_date || toParts(booking.start_datetime).date} ${requested.pickup_time || ""}`.trim() : "—")
              : ct === "return_date" || ct === "return_time" ? (requested.return_date || requested.return_time ? `${requested.return_date || toParts(booking.end_datetime).date} ${requested.return_time || ""}`.trim() : "—")
              : ct === "pickup_location" ? (requested.pickup_location || "—")
              : ct === "return_location" ? (requested.return_location || "—")
              : ct === "vehicle" ? (requested.vehicle_note || "A different vehicle") : "—";
            return (
              <div key={ct} className="grid sm:grid-cols-2 gap-3 text-sm" data-testid={`change-review-row-${ct}`}>
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Current {CHANGE_TYPE_LABELS[ct]}</div>
                  <div className="font-medium mt-0.5">{current}</div>
                </div>
                <div className="rounded-lg border-2 border-amber-400 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700">Requested {CHANGE_TYPE_LABELS[ct]}</div>
                  <div className="font-medium mt-0.5">{req}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {note && (
        <div className="mt-3 rounded-lg bg-white border border-amber-200 p-3 text-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Customer note</div>
          <p className="mt-0.5 whitespace-pre-line">{note}</p>
        </div>
      )}

      {mode === "propose" && (
        <div className="mt-4 rounded-lg bg-white border border-amber-200 p-3 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-800">Propose different terms</div>
          {(changeTypes.includes("pickup_date") || changeTypes.includes("pickup_time")) && (
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={fields.pickup_date} onChange={setField("pickup_date")} data-testid="propose-pickup-date" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              <input type="time" value={fields.pickup_time} onChange={setField("pickup_time")} data-testid="propose-pickup-time" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </div>
          )}
          {(changeTypes.includes("return_date") || changeTypes.includes("return_time")) && (
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={fields.return_date} onChange={setField("return_date")} data-testid="propose-return-date" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              <input type="time" value={fields.return_time} onChange={setField("return_time")} data-testid="propose-return-time" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </div>
          )}
          {changeTypes.includes("pickup_location") && (
            <input value={fields.pickup_location} onChange={setField("pickup_location")} placeholder="Pickup location" data-testid="propose-pickup-location" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          )}
          {changeTypes.includes("return_location") && (
            <input value={fields.return_location} onChange={setField("return_location")} placeholder="Return location" data-testid="propose-return-location" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          )}
        </div>
      )}

      {checkResult && (
        <div className={`mt-3 rounded-lg border p-3 text-sm ${checkResult.has_critical ? "border-red-300 bg-red-50 text-red-900" : checkResult.has_warning ? "border-amber-300 bg-amber-100 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="change-review-availability-result">
          <div className="font-semibold inline-flex items-center gap-1.5">
            {checkResult.has_critical ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
            {checkResult.has_critical ? "Conflict" : checkResult.has_warning ? "Available, with a buffer warning" : "Available"}
          </div>
          {checkResult.overlaps?.map((o) => (
            <p key={o.id} className="mt-1">{o.customer_name || "Another booking"}: {fmtDate(o.start_datetime)} → {fmtDate(o.end_datetime)}</p>
          ))}
          {needsOverride && (
            <input value={overrideNote} onChange={(e) => setOverrideNote(e.target.value)} placeholder="Override note (required to proceed anyway)"
                   data-testid="change-review-override-note" className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={checkAvailability} disabled={checking} data-testid="change-review-check-availability-btn"
                className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm disabled:opacity-60">
          {checking ? "Checking…" : "Check Availability"}
        </button>
        {mode !== "propose" ? (
          <>
            <button onClick={approveChange} disabled={busy || (needsOverride && !overrideNote.trim())} data-testid="approve-change-btn"
                    className="px-3 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Approve Change
            </button>
            <button onClick={() => setMode("propose")} data-testid="propose-different-option-btn"
                    className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1.5">
              <PencilLine className="w-4 h-4" /> Propose Different Option
            </button>
          </>
        ) : (
          <>
            <button onClick={proposeDifferent} disabled={busy || (needsOverride && !overrideNote.trim())} data-testid="send-proposed-option-btn"
                    className="px-3 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60">
              Send Updated Confirmation
            </button>
            <button onClick={() => setMode(null)} className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
              Back
            </button>
          </>
        )}
        <button onClick={declineChange} disabled={busy} data-testid="decline-change-btn"
                className="px-3 py-2 rounded-lg border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 text-sm inline-flex items-center gap-1.5">
          <XCircle className="w-4 h-4" /> Decline Change
        </button>
      </div>
    </div>
  );
}
